import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  Menu,
  RefreshCw,
  Loader2,
  ScrollText,
  MessageCircleReply,
  Clock,
  BarChart3,
  Filter,
  Search,
  X
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';

const SEO_AUDIT_API =
  process.env.REACT_APP_SEO_AUDIT_API ||
  'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';

const actionLabels = {
  review_fetch: { label: 'Fetch', color: 'bg-blue-100 text-blue-700' },
  ai_draft: { label: 'AI Draft', color: 'bg-purple-100 text-purple-700' },
  status_change: {
    label: 'Status Change',
    color: 'bg-amber-100 text-amber-700'
  },
  review_delete: { label: 'Delete', color: 'bg-red-100 text-red-700' },
  review_edit: { label: 'Edit', color: 'bg-gray-100 text-gray-700' }
};

const platformColors = {
  yelp: { bg: 'bg-red-500', label: 'Yelp' },
  facebook: { bg: 'bg-blue-600', label: 'Facebook' },
  tripadvisor: { bg: 'bg-green-500', label: 'TripAdvisor' },
  bing: { bg: 'bg-cyan-500', label: 'Bing' },
  bbb: { bg: 'bg-orange-500', label: 'BBB' },
  opentable: { bg: 'bg-rose-500', label: 'OpenTable' },
  angi: { bg: 'bg-emerald-500', label: 'Angi' },
  google: { bg: 'bg-yellow-500', label: 'Google' }
};

// ─── Metric Helpers ──────────────────────────────────────

const computeMetrics = (reviews) => {
  const total = reviews.length;
  if (total === 0) {
    return {
      responseRate: 0,
      avgResponseTime: null,
      avgResponseTimeLabel: '—',
      volumeByPlatform: []
    };
  }

  // Response Rate: reviews whose status is NOT "new"
  const responded = reviews.filter(
    (r) => r.response && r.response.status && r.response.status !== 'new'
  );
  const responseRate = Math.round((responded.length / total) * 100);

  // Avg Response Time: mean(respondedAt - reviewDate) for reviews that have respondedAt
  const responseTimes = [];
  for (const r of reviews) {
    if (r.response?.respondedAt && r.reviewDate) {
      const diff =
        new Date(r.response.respondedAt).getTime() -
        new Date(r.reviewDate).getTime();
      if (diff > 0) {
        responseTimes.push(diff);
      }
    }
  }

  let avgResponseTime = null;
  let avgResponseTimeLabel = '—';
  if (responseTimes.length > 0) {
    avgResponseTime =
      responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
    avgResponseTimeLabel = formatDuration(avgResponseTime);
  }

  // Volume by Platform: group + count
  const platformCounts = {};
  for (const r of reviews) {
    const p = r.platform || 'unknown';
    platformCounts[p] = (platformCounts[p] || 0) + 1;
  }
  const volumeByPlatform = Object.entries(platformCounts)
    .map(([platform, count]) => ({ platform, count }))
    .sort((a, b) => b.count - a.count);

  return {
    responseRate,
    avgResponseTime,
    avgResponseTimeLabel,
    volumeByPlatform
  };
};

const formatDuration = (ms) => {
  const minutes = ms / 60000;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  const days = hours / 24;
  return `${days.toFixed(1)} days`;
};

// ─── Component ───────────────────────────────────────────

const AuditLog = () => {
  const { selectedClient } = useSelector(selectedClientSelector);
  const { sidebarOpen, setSidebarOpen } = useSidebar();

  const [logs, setLogs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isVolumeModalOpen, setIsVolumeModalOpen] = useState(false);

  // ─── Filter State ───────────────────────────────────────
  const [filterAction, setFilterAction] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const hasActiveFilters =
    filterAction !== 'all' ||
    filterPlatform !== 'all' ||
    filterStartDate ||
    filterEndDate ||
    filterSearch;

  const clearFilters = () => {
    setFilterAction('all');
    setFilterPlatform('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterSearch('');
  };

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (filterAction !== 'all' && log.action !== filterAction) return false;
      if (
        filterPlatform !== 'all' &&
        (log.platform || '').toLowerCase() !== filterPlatform
      )
        return false;
      if (filterStartDate) {
        const logDate = new Date(log.timestamp);
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (logDate < start) return false;
      }
      if (filterEndDate) {
        const logDate = new Date(log.timestamp);
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (logDate > end) return false;
      }
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const matchDetails = (log.details || '').toLowerCase().includes(q);
        const matchReviewId = (log.reviewId || '').toLowerCase().includes(q);
        const matchPlatform = (log.platform || '').toLowerCase().includes(q);
        if (!matchDetails && !matchReviewId && !matchPlatform) return false;
      }
      return true;
    });
  }, [
    logs,
    filterAction,
    filterPlatform,
    filterStartDate,
    filterEndDate,
    filterSearch
  ]);

  const fetchData = useCallback(async () => {
    const locationId = selectedClient?.id || selectedClient?.currentClientId;
    if (!locationId) return;
    setLoading(true);
    try {
      const [logsRes, reviewsRes] = await Promise.all([
        axios.get(`${SEO_AUDIT_API}/reviews/audit-logs`, {
          params: { locationId: String(locationId), limit: 200 }
        }),
        axios.get(`${SEO_AUDIT_API}/reviews`, {
          params: { locationId: String(locationId) }
        })
      ]);
      setLogs(logsRes.data?.logs || []);
      setReviews(reviewsRes.data?.reviews || []);
    } catch (err) {
      console.error('Error fetching audit data:', err);
      setLogs([]);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const metrics = useMemo(() => computeMetrics(reviews), [reviews]);

  return (
    <>
      <header className="sticky top-0 z-10 bg-card">
        <div className="px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!sidebarOpen && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="h-10 w-10 border-0 hover:bg-gray-100/80"
                style={{
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
                }}>
                <Menu className="h-5 w-5" style={{ color: '#3766a8' }} />
              </Button>
            )}
            <div style={{ marginBottom: '0' }}>
              <h1
                className="text-xl font-semibold"
                style={{ marginBottom: '0.25rem' }}>
                Audit Log
              </h1>
              <p
                className="text-sm text-muted-foreground"
                style={{ marginBottom: '0' }}>
                Track all review management actions with timestamps
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1.5"
            onClick={fetchData}
            disabled={loading}>
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>
      </header>

      <main className="p-6 space-y-6">
        {/* ─── Metrics Cards ─────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Response Rate */}
          <Card className="rounded-2xl border-gray-100 shadow-sm">
            <CardContent className="pt-6 pb-5 px-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <MessageCircleReply className="h-4.5 w-4.5 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Response Rate
                  </span>
                </div>
              </div>
              {loading ? (
                <div className="h-9 w-24 bg-gray-200 rounded animate-pulse" />
              ) : (
                <>
                  <p className="text-3xl font-bold tracking-tight">
                    {metrics.responseRate}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {
                      reviews.filter(
                        (r) => r.response?.status && r.response.status !== 'new'
                      ).length
                    }{' '}
                    of {reviews.length} reviews responded
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Avg Response Time */}
          <Card className="rounded-2xl border-gray-100 shadow-sm">
            <CardContent className="pt-6 pb-5 px-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Clock className="h-4.5 w-4.5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Avg Response Time
                  </span>
                </div>
              </div>
              {loading ? (
                <div className="h-9 w-24 bg-gray-200 rounded animate-pulse" />
              ) : (
                <>
                  <p className="text-3xl font-bold tracking-tight">
                    {metrics.avgResponseTimeLabel}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {metrics.avgResponseTime !== null
                      ? 'Average time from review to response'
                      : 'No responded reviews yet'}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Review Volume by Platform */}
          <Card
            className="rounded-2xl border-gray-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setIsVolumeModalOpen(true)}>
            <CardContent className="pt-6 pb-5 px-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-xl bg-purple-50 flex items-center justify-center">
                    <BarChart3 className="h-4.5 w-4.5 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Review Volume
                  </span>
                </div>
                <span className="text-sm font-semibold">
                  {reviews.length} total
                </span>
              </div>
              {loading ? (
                <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
              ) : reviews.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No reviews fetched yet
                </p>
              ) : (
                <>
                  {/* Stacked bar */}
                  <div className="flex h-3 rounded-full overflow-hidden mb-3">
                    {metrics.volumeByPlatform.map(({ platform, count }) => {
                      const pct = (count / reviews.length) * 100;
                      const color =
                        platformColors[platform]?.bg || 'bg-gray-400';
                      return (
                        <div
                          key={platform}
                          className={`${color} transition-all`}
                          style={{ width: `${pct}%` }}
                          title={`${
                            platformColors[platform]?.label || platform
                          }: ${count}`}
                        />
                      );
                    })}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {metrics.volumeByPlatform.map(({ platform, count }) => {
                      const color =
                        platformColors[platform]?.bg || 'bg-gray-400';
                      const label = platformColors[platform]?.label || platform;
                      return (
                        <div
                          key={platform}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span
                            className={`inline-block h-2 w-2 rounded-full ${color}`}
                          />
                          {label}{' '}
                          <span className="font-medium text-foreground">
                            {count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ─── Activity Timeline ─────────────────────────── */}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg font-semibold">
                  Activity Timeline
                </CardTitle>
                <Badge className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full border-0 shadow-none">
                  {filteredLogs.length}
                  {filteredLogs.length !== logs.length
                    ? ` / ${logs.length}`
                    : ''}
                </Badge>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs gap-1 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}>
                  <X className="h-3 w-3" />
                  Clear Filters
                </Button>
              )}
            </div>

            {/* ─── Filter Bar ─── */}
            <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Filter className="h-3.5 w-3.5" />
                Filters
              </div>

              {/* Action Filter */}
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="h-8 rounded-lg border border-gray-200 bg-white px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="all">All Actions</option>
                <option value="review_fetch">Fetch</option>
                <option value="ai_draft">AI Draft</option>
                <option value="status_change">Status Change</option>
                <option value="review_delete">Delete</option>
                <option value="review_edit">Edit</option>
              </select>

              {/* Platform Filter */}
              <select
                value={filterPlatform}
                onChange={(e) => setFilterPlatform(e.target.value)}
                className="h-8 rounded-lg border border-gray-200 bg-white px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="all">All Platforms</option>
                <option value="yelp">Yelp</option>
                <option value="facebook">Facebook</option>
                <option value="tripadvisor">TripAdvisor</option>
                <option value="bing">Bing</option>
                <option value="bbb">BBB</option>
                <option value="opentable">OpenTable</option>
                <option value="angi">Angi</option>
                <option value="google">Google</option>
              </select>

              {/* Date Range */}
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="From"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="To"
                />
              </div>

              {/* Search */}
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  placeholder="Search details, review ID..."
                  className="h-8 pl-8 text-xs rounded-lg bg-white"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading logs...
                </span>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-16">
                <ScrollText className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
                <p className="text-sm text-muted-foreground">
                  No audit logs yet. Actions will appear here as you fetch,
                  draft, approve, or delete reviews.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold w-[200px]">
                        Time
                      </TableHead>
                      <TableHead className="text-xs font-semibold w-[150px]">
                        Action
                      </TableHead>
                      <TableHead className="text-xs font-semibold w-[120px]">
                        Platform
                      </TableHead>
                      <TableHead className="text-xs font-semibold w-[180px]">
                        Review ID
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Details
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map((log, idx) => {
                      const actionInfo = actionLabels[log.action] || {
                        label: log.action,
                        color: 'bg-gray-100 text-gray-700'
                      };
                      return (
                        <TableRow
                          key={`${log.timestamp}-${idx}`}
                          className="hover:bg-muted/30">
                          <TableCell className="text-sm text-muted-foreground py-3">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge
                              className={`${actionInfo.color} text-xs px-2.5 py-0.5 rounded-full border-0 shadow-none font-medium`}>
                              {actionInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm py-3 capitalize">
                            {log.platform || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground py-3 font-mono">
                            {log.reviewId
                              ? log.reviewId.length > 20
                                ? `${log.reviewId.slice(0, 20)}…`
                                : log.reviewId
                              : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground py-3">
                            {log.details || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* ─── Review Volume Modal ─────────────────────────── */}
      <Dialog open={isVolumeModalOpen} onOpenChange={setIsVolumeModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Review Volume Analysis
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Detailed breakdown of your {reviews.length} reviews by platform.
            </p>
          </DialogHeader>

          <div className="py-2">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : reviews.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">
                No reviews found for this client.
              </p>
            ) : (
              <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                <div className="space-y-5">
                  {metrics.volumeByPlatform.map(({ platform, count }) => {
                    const pct = ((count / reviews.length) * 100).toFixed(1);
                    const color = platformColors[platform]?.bg || 'bg-gray-400';
                    const label = platformColors[platform]?.label || platform;

                    return (
                      <div key={platform} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-semibold text-foreground flex items-center gap-2">
                            <div
                              className={`h-3.5 w-3.5 rounded-full ${color}`}
                            />
                            {label}
                          </span>
                          <span className="text-muted-foreground font-medium">
                            <span className="text-foreground">{count}</span> (
                            {pct}%)
                          </span>
                        </div>
                        <div className="h-4 w-full bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full ${color} transition-all duration-700`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuditLog;
